import { supabase } from '../config/supabase';
import { ApiResponse } from '../types';

function validateLimit(limit: number): number {
  return Math.min(Math.max(1, Math.floor(limit)), 100);
}

/**
 * エラーメッセージを分類し、ユーザーフレンドリーなメッセージに変換
 *
 * @param error - Supabaseまたはネットワークエラーオブジェクト
 * @param defaultMessage - カテゴリ化できない場合のデフォルトメッセージ
 * @param errorId - エラー追跡用のID（本番環境でのログ相互参照に使用）
 * @returns ユーザー向けのエラーメッセージ（開発環境では詳細付き）
 */
function categorizeErrorMessage(error: any, defaultMessage: string, errorId?: string): string {
  const errorMessage = error?.message || String(error);
  const errorIdSuffix = errorId ? ` [エラーID: ${errorId}]` : '';

  if (errorMessage.includes('ETIMEDOUT') || errorMessage.includes('timeout')) {
    return `${defaultMessage} ネットワーク接続が不安定です。しばらく待ってから再試行してください。${errorIdSuffix}`;
  }

  if (errorMessage.includes('JWT') || errorMessage.includes('auth')) {
    return `${defaultMessage} セッションの有効期限が切れました。ページをリロードしてください。${errorIdSuffix}`;
  }

  if (errorMessage.includes('permission') || errorMessage.includes('RLS')) {
    return `${defaultMessage} データへのアクセス権限がありません。管理者に連絡してください。${errorIdSuffix}`;
  }

  if (errorMessage.includes('database') || errorMessage.includes('query')) {
    return `${defaultMessage} データベースエラーが発生しました。ページをリロードして再試行してください。${errorIdSuffix}`;
  }

  return `${defaultMessage}${process.env.NODE_ENV === 'development' ? ` (${errorMessage})` : errorIdSuffix}`;
}

export interface DashboardMetrics {
  totalEngineers: number;
  availableEngineers: number;
  totalProjects: number;
  activeProjects: number;
  pendingProjects: number;
  completedProjects: number;
  totalAssignments: number;
  activeAssignments: number;
  averageMatchScore: number;
  topSkills: Array<{
    skillName: string;
    count: number;
  }>;
  monthlyRevenue: number;
  conversionRate: number;
}

export interface RecentActivity {
  id: string;
  type: 'project_created' | 'engineer_registered' | 'assignment_created' | 'assignment_confirmed';
  title: string;
  description: string;
  timestamp: string;
  status?: string;
  priority?: string;
}

export interface ProjectSummary {
  id: number;
  title: string;
  client_name?: string;
  status: string;
  priority: string;
  budget?: number;
  skills: string[];
  created_at: string;
  urgent: boolean;
}

export interface EngineerSummary {
  id: number;
  name: string;
  availability_status: string;
  experience_years?: number;
  hourly_rate?: number;
  skills: string[];
  location?: string;
}

export interface RecommendedMatch {
  id: number;
  engineer: {
    id: number;
    name: string;
    skills: string[];
    experience_years?: number;
    match_percentage: number;
  };
  project: {
    id: number;
    title: string;
    budget?: number;
    duration_months?: number;
    skills: string[];
    priority: string;
  };
}

export const dashboardService = {
  // Get dashboard metrics
  async getMetrics(): Promise<ApiResponse<DashboardMetrics>> {
    try {
      // 全12クエリを並列実行（async-parallel ルール適用）
      const [
        engineersResult,
        availableEngineersResult,
        projectsResult,
        activeProjectsResult,
        pendingProjectsResult,
        completedProjectsResult,
        assignmentsResult,
        activeAssignmentsResult,
        skillsResult,
        revenueResult,
        proposedResult,
        confirmedResult
      ] = await Promise.all([
        supabase.from('engineers').select('*', { count: 'exact', head: true }),
        supabase.from('engineers').select('*', { count: 'exact', head: true }).eq('availability_status', 'available'),
        supabase.from('projects').select('*', { count: 'exact', head: true }),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'pending'),
        supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'completed'),
        supabase.from('project_assignments').select('*', { count: 'exact', head: true }),
        supabase.from('project_assignments').select('*', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('engineer_skills').select('skill_name'),
        supabase.from('project_assignments').select('hourly_rate').eq('status', 'active').not('hourly_rate', 'is', null),
        supabase.from('project_assignments').select('*', { count: 'exact', head: true }).eq('status', 'proposed'),
        supabase.from('project_assignments').select('*', { count: 'exact', head: true }).eq('status', 'confirmed')
      ]);

      // エラーチェック
      const errors = [
        { result: engineersResult, id: 'METRICS_ENGINEERS_COUNT_FAILED', msg: 'エンジニア数の取得に失敗しました' },
        { result: availableEngineersResult, id: 'METRICS_AVAILABLE_ENGINEERS_COUNT_FAILED', msg: '稼働可能エンジニア数の取得に失敗しました' },
        { result: projectsResult, id: 'METRICS_PROJECTS_COUNT_FAILED', msg: '案件総数の取得に失敗しました' },
        { result: activeProjectsResult, id: 'METRICS_ACTIVE_PROJECTS_COUNT_FAILED', msg: 'アクティブ案件数の取得に失敗しました' },
        { result: pendingProjectsResult, id: 'METRICS_PENDING_PROJECTS_COUNT_FAILED', msg: '保留案件数の取得に失敗しました' },
        { result: completedProjectsResult, id: 'METRICS_COMPLETED_PROJECTS_COUNT_FAILED', msg: '完了案件数の取得に失敗しました' },
        { result: assignmentsResult, id: 'METRICS_ASSIGNMENTS_COUNT_FAILED', msg: 'アサイン総数の取得に失敗しました' },
        { result: activeAssignmentsResult, id: 'METRICS_ACTIVE_ASSIGNMENTS_COUNT_FAILED', msg: 'アクティブアサイン数の取得に失敗しました' },
        { result: skillsResult, id: 'METRICS_SKILLS_QUERY_FAILED', msg: 'スキルデータの取得に失敗しました' },
        { result: revenueResult, id: 'METRICS_REVENUE_QUERY_FAILED', msg: '売上データの取得に失敗しました' },
        { result: proposedResult, id: 'METRICS_PROPOSED_ASSIGNMENTS_COUNT_FAILED', msg: '提案アサイン数の取得に失敗しました' },
        { result: confirmedResult, id: 'METRICS_CONFIRMED_ASSIGNMENTS_COUNT_FAILED', msg: '確定アサイン数の取得に失敗しました' }
      ];

      for (const { result, id, msg } of errors) {
        if (result.error) {
          console.error(`❌ ${msg}:`, { errorId: id, error: result.error, timestamp: new Date().toISOString() });
          throw new Error(msg);
        }
      }

      // スキル集計
      const skillCounts: { [key: string]: number } = {};
      skillsResult.data?.forEach(skill => {
        skillCounts[skill.skill_name] = (skillCounts[skill.skill_name] || 0) + 1;
      });
      const topSkills = Object.entries(skillCounts)
        .map(([skillName, count]) => ({ skillName, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 5);

      // 月間売上試算: 時給 × 想定稼働時間（160h = 40h/週 × 4週）
      const monthlyRevenue = revenueResult.data?.reduce((total, assignment) => {
        return total + (assignment.hourly_rate * 160);
      }, 0) || 0;

      // 成約率の計算: 確定アサイン数 ÷ 提案アサイン数
      const conversionRate = proposedResult.count && confirmedResult.count ?
        Math.round((confirmedResult.count / proposedResult.count) * 100) : 0;

      const metrics: DashboardMetrics = {
        totalEngineers: engineersResult.count || 0,
        availableEngineers: availableEngineersResult.count || 0,
        totalProjects: projectsResult.count || 0,
        activeProjects: activeProjectsResult.count || 0,
        pendingProjects: pendingProjectsResult.count || 0,
        completedProjects: completedProjectsResult.count || 0,
        totalAssignments: assignmentsResult.count || 0,
        activeAssignments: activeAssignmentsResult.count || 0,
        // TODO: 実装予定 - 現在は仮値75%を返却
        // マッチングスコアの実計算にはスキル一致度、経験年数、稼働率等の
        // 複数要素を考慮したアルゴリズムが必要
        averageMatchScore: 75,
        topSkills,
        monthlyRevenue,
        conversionRate
      };

      return { data: metrics, error: null };
    } catch (error) {
      console.error('❌ getMetrics failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_METRICS_FAILED',
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, 'メトリクスの取得に失敗しました。', 'DASHBOARD_SERVICE_GET_METRICS_FAILED')
      };
    }
  },

  async getRecentProjects(limit: number = 5): Promise<ApiResponse<ProjectSummary[]>> {
    try {
      const validatedLimit = validateLimit(limit);

      const { data: projects, error } = await supabase
        .from('projects')
        .select(`
          id,
          title,
          client_name,
          status,
          priority,
          budget,
          created_at,
          project_skills (skill_name)
        `)
        .order('created_at', { ascending: false })
        .limit(validatedLimit);

      if (error) {
        console.error('❌ Supabaseクエリエラー:', {
          errorId: 'DASHBOARD_SERVICE_PROJECTS_QUERY_FAILED',
          errorCode: error.code,
          errorMessage: error.message,
          timestamp: new Date().toISOString()
        });
        return {
          data: null,
          error: categorizeErrorMessage(error, '案件の取得に失敗しました。', 'DASHBOARD_SERVICE_PROJECTS_QUERY_FAILED')
        };
      }

      const projectSummaries: ProjectSummary[] = projects?.map((project: any) => ({
        id: project.id,
        title: project.title,
        client_name: project.client_name,
        status: project.status,
        priority: project.priority,
        budget: project.budget,
        skills: project.project_skills?.map((ps: any) => ps.skill_name) || [],
        created_at: project.created_at,
        urgent: project.priority === 'urgent' || project.priority === 'high'
      })) || [];

      return { data: projectSummaries, error: null };
    } catch (error) {
      console.error('❌ getRecentProjects failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_PROJECTS_FAILED',
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, '案件リストの取得に失敗しました。', 'DASHBOARD_SERVICE_GET_PROJECTS_FAILED')
      };
    }
  },

  async getAvailableEngineers(limit: number = 5): Promise<ApiResponse<EngineerSummary[]>> {
    try {
      const validatedLimit = validateLimit(limit);

      const { data: engineers, error } = await supabase
        .from('engineers')
        .select(`
          id,
          name,
          availability_status,
          experience_years,
          hourly_rate,
          location,
          engineer_skills (skill_name)
        `)
        .eq('availability_status', 'available')
        .order('created_at', { ascending: false })
        .limit(validatedLimit);

      if (error) {
        console.error('❌ Supabaseクエリエラー:', {
          errorId: 'DASHBOARD_SERVICE_ENGINEERS_QUERY_FAILED',
          errorCode: error.code,
          errorMessage: error.message,
          timestamp: new Date().toISOString()
        });
        return {
          data: null,
          error: categorizeErrorMessage(error, 'エンジニアの取得に失敗しました。', 'DASHBOARD_SERVICE_ENGINEERS_QUERY_FAILED')
        };
      }

      const engineerSummaries: EngineerSummary[] = engineers?.map((engineer: any) => ({
        id: engineer.id,
        name: engineer.name,
        availability_status: engineer.availability_status,
        experience_years: engineer.experience_years,
        hourly_rate: engineer.hourly_rate,
        location: engineer.location,
        skills: engineer.engineer_skills?.map((es: any) => es.skill_name) || []
      })) || [];

      return { data: engineerSummaries, error: null };
    } catch (error) {
      console.error('❌ getAvailableEngineers failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_ENGINEERS_FAILED',
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, 'エンジニアリストの取得に失敗しました。', 'DASHBOARD_SERVICE_GET_ENGINEERS_FAILED')
      };
    }
  },

  async getRecentActivity(limit: number = 10): Promise<ApiResponse<RecentActivity[]>> {
    try {
      const validatedLimit = validateLimit(limit);

      // 3つのクエリを並列実行（async-parallel ルール適用）
      const [projectsResult, engineersResult, assignmentsResult] = await Promise.all([
        supabase
          .from('projects')
          .select('id, title, status, priority, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('engineers')
          .select('id, name, availability_status, created_at')
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('project_assignments')
          .select(`
            id, status, role, created_at,
            projects (title),
            engineers (name)
          `)
          .order('created_at', { ascending: false })
          .limit(5)
      ]);

      if (projectsResult.error) {
        console.error('❌ 案件アクティビティの取得に失敗:', {
          errorId: 'ACTIVITY_PROJECTS_QUERY_FAILED',
          error: projectsResult.error,
          timestamp: new Date().toISOString()
        });
        throw new Error('案件アクティビティの取得に失敗しました');
      }

      if (engineersResult.error) {
        console.error('❌ エンジニアアクティビティの取得に失敗:', {
          errorId: 'ACTIVITY_ENGINEERS_QUERY_FAILED',
          error: engineersResult.error,
          timestamp: new Date().toISOString()
        });
        throw new Error('エンジニアアクティビティの取得に失敗しました');
      }

      if (assignmentsResult.error) {
        console.error('❌ アサインアクティビティの取得に失敗:', {
          errorId: 'ACTIVITY_ASSIGNMENTS_QUERY_FAILED',
          error: assignmentsResult.error,
          timestamp: new Date().toISOString()
        });
        throw new Error('アサインアクティビティの取得に失敗しました');
      }

      const activities: RecentActivity[] = [];

      projectsResult.data?.forEach(project => {
        activities.push({
          id: `project-${project.id}`,
          type: 'project_created',
          title: `新着案件: ${project.title}`,
          description: `優先度: ${project.priority}`,
          timestamp: project.created_at,
          status: project.status,
          priority: project.priority
        });
      });

      engineersResult.data?.forEach(engineer => {
        activities.push({
          id: `engineer-${engineer.id}`,
          type: 'engineer_registered',
          title: `エンジニア登録: ${engineer.name}`,
          description: `状態: ${engineer.availability_status}`,
          timestamp: engineer.created_at,
          status: engineer.availability_status
        });
      });

      assignmentsResult.data?.forEach((assignment: any) => {
        activities.push({
          id: `assignment-${assignment.id}`,
          type: assignment.status === 'confirmed' ? 'assignment_confirmed' : 'assignment_created',
          title: `アサイン${assignment.status === 'confirmed' ? '確定' : '提案'}: ${assignment.engineers?.name}`,
          description: `案件: ${assignment.projects?.title}`,
          timestamp: assignment.created_at,
          status: assignment.status
        });
      });

      // Sort by timestamp and limit
      activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
      const limitedActivities = activities.slice(0, validatedLimit);

      return { data: limitedActivities, error: null };
    } catch (error) {
      console.error('❌ getRecentActivity failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_ACTIVITY_FAILED',
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, 'アクティビティの取得に失敗しました。', 'DASHBOARD_SERVICE_GET_ACTIVITY_FAILED')
      };
    }
  },

  async getTopMatches(projectId?: number, limit: number = 3): Promise<ApiResponse<any[]>> {
    try {
      // projectIdが未指定の場合は空配列を返す（エラーではない）
      if (!projectId) {
        return { data: [], error: null };
      }

      const validatedLimit = validateLimit(limit);

      // マッチング候補のエンジニアを取得
      const { data, error } = await supabase
        .from('engineers')
        .select(`
          id, name, experience_years,
          engineer_skills (skill_name, skill_level)
        `)
        .eq('availability_status', 'available')
        .limit(validatedLimit);

      if (error) {
        console.error('❌ マッチング候補の取得に失敗:', {
          errorId: 'TOP_MATCHES_QUERY_FAILED',
          projectId,
          error,
          timestamp: new Date().toISOString()
        });
        return {
          data: null,
          error: categorizeErrorMessage(error, 'マッチング候補の取得に失敗しました。', 'TOP_MATCHES_QUERY_FAILED')
        };
      }

      return { data: data || [], error: null };
    } catch (error) {
      console.error('❌ getTopMatches failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_MATCHES_FAILED',
        projectId,
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, 'マッチング候補の取得に失敗しました。', 'DASHBOARD_SERVICE_GET_MATCHES_FAILED')
      };
    }
  },

  async getRecommendedMatches(limit: number = 2): Promise<ApiResponse<RecommendedMatch[]>> {
    try {
      const validatedLimit = validateLimit(limit);

      // エンジニアと案件を並列取得（async-parallel ルール適用）
      const [engineersResult, projectsResult] = await Promise.all([
        supabase
          .from('engineers')
          .select(`
            id, name, experience_years,
            engineer_skills (skill_name, skill_level)
          `)
          .eq('availability_status', 'available')
          .limit(5),
        supabase
          .from('projects')
          .select(`
            id, title, budget, priority, start_date, end_date,
            project_skills (skill_name, required_years)
          `)
          .in('status', ['pending', 'active'])
          .limit(5)
      ]);

      if (engineersResult.error) {
        console.error('❌ マッチング用エンジニアの取得に失敗:', {
          errorId: 'MATCHES_ENGINEERS_QUERY_FAILED',
          error: engineersResult.error,
          timestamp: new Date().toISOString()
        });
        throw new Error('エンジニアデータの取得に失敗しました');
      }

      if (projectsResult.error) {
        console.error('❌ マッチング用案件の取得に失敗:', {
          errorId: 'MATCHES_PROJECTS_QUERY_FAILED',
          error: projectsResult.error,
          timestamp: new Date().toISOString()
        });
        throw new Error('案件データの取得に失敗しました');
      }

      const engineers = engineersResult.data;
      const projects = projectsResult.data;

      const matches: RecommendedMatch[] = [];

      if (engineers && projects && engineers.length > 0 && projects.length > 0) {
        // Create matches based on skill overlap
        for (let i = 0; i < Math.min(validatedLimit, engineers.length, projects.length); i++) {
          const engineer = engineers[i];
          const project = projects[i];
          
          const engineerSkills = engineer.engineer_skills?.map((es: any) => es.skill_name) || [];
          const projectSkills = project.project_skills?.map((ps: any) => ps.skill_name) || [];
          
          // Calculate duration in months
          let durationMonths = null;
          if (project.start_date && project.end_date) {
            const startDate = new Date(project.start_date);
            const endDate = new Date(project.end_date);
            durationMonths = Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 30));
          }
          
          // Calculate match percentage based on skill overlap
          const commonSkills = engineerSkills.filter((skill: string) => projectSkills.includes(skill));
          const matchPercentage = projectSkills.length > 0
            ? Math.round((commonSkills.length / projectSkills.length) * 100)
            : 0;

          matches.push({
            id: i + 1,
            engineer: {
              id: engineer.id,
              name: engineer.name,
              skills: engineerSkills,
              experience_years: engineer.experience_years,
              match_percentage: matchPercentage
            },
            project: {
              id: project.id,
              title: project.title,
              budget: project.budget,
              duration_months: durationMonths ?? undefined,
              skills: projectSkills,
              priority: project.priority
            }
          });
        }
      }

      return { data: matches, error: null };
    } catch (error) {
      console.error('❌ getRecommendedMatches failed:', {
        errorId: 'DASHBOARD_SERVICE_GET_RECOMMENDED_MATCHES_FAILED',
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        timestamp: new Date().toISOString()
      });
      return {
        data: null,
        error: categorizeErrorMessage(error, 'おすすめマッチングの取得に失敗しました。', 'DASHBOARD_SERVICE_GET_RECOMMENDED_MATCHES_FAILED')
      };
    }
  }
};
